/**
 * @file index.tsx
 *
 * @fileoverview Renders Bookmark component, removes and deletes resume bookmark.
 * This component can be used inside of BookmarkedResumesContext and independently of it.
 * If we want a component to update the context we must set reloadAfterMutation to
 * true, otherwise, the component reloads the page to update resume bookmark data.
 */
import REMOVE_RESUME_BOOKMARK from '~/jobiqo/components/BookmarkedResumes/graphql/mutations/removeResumeBookmark.gql';
import ADD_RESUME_BOOKMARK from '~/jobiqo/components/BookmarkedResumes/graphql/mutations/addResumeBookmark.gql';
import { useMutation } from '@apollo/client';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Resume } from '~/jobiqo/types/types';
import { FormattedMessage } from 'react-intl';
import {
  BookmarkedResumesContext,
  BookmarkedResumesContextInterface,
} from '~/jobiqo/components/BookmarkedResumes/context';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { analyticsDispatch, EVENTS } from '~/jobiqo/lib/analytics';
import { isApplicant, isRecruiter } from '~/jobiqo/lib/auth';
import ResumeBookmarkPlacement from '~/jobiqo/components/BookmarkedResumes/enums/ResumeBookmarkPlacement';
import { Bookmark } from '~/vcl/atoms/28-bookmark';
import { Alert } from '~/vcl/atoms/18-alert';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { useConfig } from '~/jobiqo/context/config';
import { getResumeAnalyticsDimensions } from '~/jobiqo/components/Resume/resume_analytics';

export interface ResumeBookmarkProps {
  /**
   * The bookmark placement.
   *
   * @default ResumeBookmarkPlacement.RESUME_TEASER
   */
  placement?: ResumeBookmarkPlacement;
  /**
   * The bookmarked resume.
   */
  resume: Resume;
  /**
   * The remove/add bookmark mutation callback.
   */
  bookmarkMutationCallback?(): void;
}

export function ResumeBookmark({
  placement = ResumeBookmarkPlacement.RESUME_TEASER,
  resume,
  bookmarkMutationCallback,
}: ResumeBookmarkProps) {
  const {
    bookmarkedResumes,
    setBookmarkedResumes,
    getBookmarkedResume,
    setUpdateBoomarkResponse,
    dispatchBookmarkUpdateFlag,
    updateBookmarkResponse,
  }: BookmarkedResumesContextInterface = useContext(BookmarkedResumesContext);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const { BOOKMARK, BOOKMARKED } = getCommonTranslations();
  const { user }: UserContextInterface = useContext(UserContext);
  const isBookmarked: boolean = useMemo(
    () => !!(resume?.id && getBookmarkedResume(resume.id)),
    [resume?.id, getBookmarkedResume],
  );

  const { getConfigByName } = useConfig();
  const RESUME_BOOKMARK_ENABLED: boolean = !!getConfigByName(
    'jobiqo_resume_bookmark',
  )?.value[0];

  // Hide bookmark from the applicant or when the resume feature is disabled.
  if (!RESUME_BOOKMARK_ENABLED || (user && isApplicant(user))) {
    return null;
  }

  const [updateBookmark] = useMutation(
    isBookmarked ? REMOVE_RESUME_BOOKMARK : ADD_RESUME_BOOKMARK,
    {
      variables: {
        resumeId: resume.id,
      },
      onCompleted({ bookmark }) {
        if (!isBookmarked && user && isRecruiter(user)) {
          analyticsDispatch(
            EVENTS.RESUME_BOOKMARK,
            getResumeAnalyticsDimensions(resume, user),
          );
        }
        setShowRemoveConfirm(false);
        setUpdateBoomarkResponse(bookmark);
        setBookmarkedResumes(
          isBookmarked
            ? // Remove the resume.
              bookmarkedResumes.filter(
                (bookmarkedResume) => bookmarkedResume.id !== resume.id,
              )
            : // Add a new bookmarked resume.
              [...bookmarkedResumes, resume],
        );
      },
    },
  );

  useEffect(() => {
    if (updateBookmarkResponse) {
      // Whenever we add/remove a bookmark, we must either dispatch the
      // bookmark update flag or let the parent component handle that case.
      bookmarkMutationCallback?.() ?? dispatchBookmarkUpdateFlag();
    }
  }, [updateBookmarkResponse]);

  return (
    <div
      data-testid={`resume-${resume.id}-${
        isBookmarked ? 'unbookmark' : 'bookmark'
      }`}
    >
      <Bookmark
        variant={
          placement === ResumeBookmarkPlacement.RESUME_DETAIL ? 'badge' : 'icon'
        }
        isBookmarked={isBookmarked}
        onBookmark={() =>
          isBookmarked ? setShowRemoveConfirm(true) : updateBookmark()
        }
        text={isBookmarked ? BOOKMARKED : BOOKMARK}
      />
      <Alert
        isShown={showRemoveConfirm}
        onConfirm={updateBookmark}
        onClose={() => setShowRemoveConfirm(false)}
        confirmText={
          <FormattedMessage
            id="bookmark_resumes_confirm_remove"
            defaultMessage="Yes, remove"
          />
        }
        cancelText={<FormattedMessage id="cancel" defaultMessage="Cancel" />}
        alertLabel={
          <FormattedMessage
            id="bookmark_resumes_confirm_remove_label"
            defaultMessage="remove"
          />
        }
      >
        <FormattedMessage
          id="bookmark_resumes_confirm_remove_text"
          defaultMessage="Are you sure you want to remove resume bookmark?"
        />
      </Alert>
    </div>
  );
}
